import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { SeeqLogo2026 } from '@/core/SeeqLogoOrange.atom';
import { getWorkbooksLink, returnToPreviousState } from '@/main/routing.utilities';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { formatMessage } from '@/utilities/logger.utilities';
import { notifyError } from '@/utilities/screenshot.utilities';

interface LoadErrorProps {
  header: string;
  message1: string;
  message2: string;
  retryInterval?: number;
  /**
   * Called once, `retryInterval` milliseconds after the page renders. Defaults to returning to the page that sent the
   * user here.
   */
  retry?: () => void;
}

export const LoadError = ({
  header,
  message1,
  message2,
  retryInterval,
  retry = returnToPreviousState,
}: LoadErrorProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    notifyError(formatMessage`Could not load page: ${message1}. ${message2}`);
  }, [message1, message2]);

  useEffect(() => {
    if (!headlessRenderMode() && retryInterval) {
      const timeout = setTimeout(() => retry(), retryInterval);
      return () => clearTimeout(timeout);
    }
  }, [retryInterval, retry]);

  return (
    <div className="flexColumnContainer fullViewport splashScreen tw:items-center tw:justify-center tw:text-black">
      <div className="container tw:mr-0 tw:ml-0">
        <div className="row tw:justify-center">
          <div className="span12">
            <div className="jumbotron text-center">
              <p>
                <SeeqLogo2026 />
              </p>
              <h2 className="tw:mb-sq-10">{t(header)}</h2>
              <p>
                <span>{t(message1)}</span>
              </p>
              <p>
                <span>{t(message2)}</span>
              </p>
              {retryInterval ? <IconWithSpinner testId="loadErrorLoadingIcon" spinning={true} /> : null}
              <p>
                <Link to={getWorkbooksLink()} reloadDocument={true}>
                  {t('UNAUTHORIZED.WORKBOOK_OVERVIEW')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
